import {
  CreateDepotRequest,
  DepotPricingPreference,
  DepotPricingPreferenceRequest,
  DepotResponse,
  DepotsSpendingLimitPayload,
  DepotsSpendingLimitRequest,
  EditDepotRequest,
  GetDepotProfileRequest,
  GetDepotProfileResponse,
  GetFlagsRequest,
  GetSingleFlagRequest,
  RedFlagResponse,
  ResolveRedFlagRequest,
  SingleDepot,
  SingleRedFlagResponse
} from "../../../types/depots";
import { ApiResponseMetadata, GenericResponse } from "../../../types/shared";
import { baseAPI } from "../../api";

const depotsEndpoints = baseAPI.injectEndpoints({
  endpoints: (builder) => ({
    depots: builder.query<DepotResponse, void>({
      providesTags: ["Depot", "CreateDepot"],
      query: () => ({
        url: "/depots",
        method: "GET"
      })
    }),

    createDepot: builder.mutation<GenericResponse, CreateDepotRequest>({
      invalidatesTags: ["CreateDepot"],
      query: (data) => ({
        url: `/depots`,
        method: "POST",
        body: data
      })
    }),

    addSpendingLimit: builder.mutation<void, DepotsSpendingLimitRequest>({
      invalidatesTags: ["GetDepotsSpendingLimit"],
      query: (data) => ({
        url: `/depots/spending-limits`,
        method: "POST",
        body: data
      })
    }),

    depotProfile: builder.query<
      GetDepotProfileResponse,
      GetDepotProfileRequest
    >({
      providesTags: ["DepotProfile"],
      query: ({ id, start, end }) => ({
        url: `/depots/${id}/profile?start=${start}&end=${end}`,
        method: "GET"
      })
    }),

    getFlags: builder.query<RedFlagResponse, GetFlagsRequest>({
      providesTags: [],
      query: ({ id, start, end, search, page, size }) => ({
        url: `/depots/${id}/red-flags?start=${start || ""}&end=${
          end || ""
        }&search=${search || ""}&page=${page || 0}&size=${size || 10}`,
        method: "GET"
      })
    }),

    editDepot: builder.mutation<GenericResponse, EditDepotRequest>({
      invalidatesTags: ["DepotProfile"],
      query: (DTO) => ({
        url: `/depots/${DTO.id}`,
        method: "PUT",
        body: DTO
      })
    }),

    recalculateDepotBalance: builder.mutation<
      ApiResponseMetadata<SingleDepot>,
      { depotId: number }
    >({
      invalidatesTags: ["Depot"],
      query: (DTO) => ({
        url: `/depots/re-calculate/${DTO.depotId}`,
        method: "PUT",
        body: DTO
      })
    }),

    getSingleFlag: builder.query<SingleRedFlagResponse, GetSingleFlagRequest>({
      providesTags: ["GetSingleFlag"],
      query: ({ id, redFlagId }) => ({
        url: `/depots/${id}/red-flags/${redFlagId}`,
        method: "GET"
      })
    }),

    getDepotsSpendingLimit: builder.query<
      ApiResponseMetadata<DepotsSpendingLimitPayload[]>,
      void
    >({
      providesTags: ["GetDepotsSpendingLimit"],
      query: () => ({
        url: `/depots/spending-limits/current`,
        method: "GET"
      })
    }),

    resolveRedFlag: builder.mutation<GenericResponse, ResolveRedFlagRequest>({
      invalidatesTags: ["GetSingleFlag"],
      query: (DTO) => ({
        url: `/depots/${DTO.id}/red-flags/${DTO.redFlagId}/resolve`,
        method: "PATCH",
        body: DTO
      })
    }),

    justifyRedFlag: builder.mutation<GenericResponse, ResolveRedFlagRequest>({
      invalidatesTags: ["GetSingleFlag"],
      query: (DTO) => ({
        url: `/depots/${DTO.id}/red-flags/${DTO.redFlagId}/justify`,
        method: "PATCH",
        body: DTO
      })
    }),

    depotPricingPreference: builder.query<
      DepotPricingPreference,
      DepotPricingPreferenceRequest
    >({
      query: (DTO) => ({
        url: `/depots/pricing-preference/${DTO?.depotId}/${DTO?.categoryId}/${DTO?.truckId}`,
        method: "GET"
      })
    })
  })
});

export const {
  useDepotsQuery,
  useCreateDepotMutation,
  useEditDepotMutation,
  useLazyDepotProfileQuery,
  useLazyGetFlagsQuery,
  useGetFlagsQuery,
  useLazyGetSingleFlagQuery,
  useResolveRedFlagMutation,
  useJustifyRedFlagMutation,
  useLazyDepotPricingPreferenceQuery,
  useRecalculateDepotBalanceMutation,
  useGetDepotsSpendingLimitQuery,
  useAddSpendingLimitMutation
} = depotsEndpoints;
